import '../../../styles/raid-control-mobile-viewport-debug.css';

export const MOBILE_CHAT_VIEWPORT_DEBUG_STORAGE_KEY = 'dororaid:chat-viewport-debug';
export const MOBILE_CHAT_VIEWPORT_DEBUG_GLOBAL_KEY = '__DORORAID_CHAT_VIEWPORT_LOG__';
export const MOBILE_CHAT_VIEWPORT_DEBUG_SCHEMA = 'pr134-v20';

const PRODUCTION_HOSTS = new Set(['doroparty.com', 'www.doroparty.com']);
const TOOLBAR_SELECTOR = '.raid-control-mobile-toolbar';
const WORKSPACE_SELECTOR = '.raid-control-workspace.mobile.view-chat';
const CHAT_INPUT_SELECTOR = [
  '.raid-control-room.mobile .raid-control-composer textarea',
  '.raid-control-mobile-chat-v2-composer textarea',
  '.doro-mobile-chat__composer textarea',
].join(',');
const CHAT_V2_SURFACE_SELECTOR = '.raid-control-mobile-chat-v2';
const CHAT_V2_COMPOSER_SELECTOR = '.raid-control-mobile-chat-v2-composer';
const FRESH_CHAT_SURFACE_SELECTOR = '.raid-control-mobile-chat-viewport';
const FRESH_CHAT_COMPOSER_SELECTOR = '.doro-mobile-chat__composer';
const FRAME_TRACE_MAX_FRAMES = 2;
const FRAME_TRACE_TRIGGER_EVENTS = new Set([
  'focus',
  'focusin',
  'focusout',
  'blur',
  'visualViewport.resize',
  'visualViewport.scroll',
  'measure.keyboard-open',
  'geometry.closed',
]);
const SUMMARY_ALWAYS_EVENTS = new Set([
  'chat.enter',
  'focus',
  'focusin',
  'focusout',
  'blur',
  'measure.keyboard-open',
  'measure.reopen',
  'geometry.closed',
]);

function isStandaloneDisplayMode() {
  return Boolean(window.matchMedia?.('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
}

function isPreviewHost() {
  return !PRODUCTION_HOSTS.has(String(window.location.hostname || '').toLowerCase());
}

function readRect(element) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return {
    top: Math.round(rect.top),
    right: Math.round(rect.right),
    bottom: Math.round(rect.bottom),
    left: Math.round(rect.left),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

function describeElement(element) {
  if (!(element instanceof Element)) return null;
  const id = element.id ? `#${element.id}` : '';
  const classes = element.classList.length ? `.${[...element.classList].join('.')}` : '';
  return `${element.tagName.toLowerCase()}${id}${classes}`;
}

function compactViewportEntry(entry) {
  const toolbarRect = entry.toolbarRect || null;
  const workspaceRect = entry.workspaceRuntimeRect || entry.workspaceRect || null;
  const surfaceRect = entry.freshChatSurfaceRect || entry.chatV2SurfaceRect || null;
  const composerRect = entry.freshChatComposerRect || entry.chatV2ComposerRect || null;
  const surfaceComputed = entry.freshChatSurfaceComputed || entry.chatV2SurfaceComputed || null;
  const freshSurfaceSelected = Boolean(entry.freshChatSurfaceRect);
  return {
    e: entry.event || null,
    t: entry.performanceNow ?? null,
    f: (entry.textareaFocusedRuntime ?? entry.textareaFocused) ? 1 : 0,
    ks: entry.freshChatKeyboardPhase || entry.keyboardStateRuntime || entry.keyboardState || null,
    kvo: entry.freshChatKeyboardVisualOpen || null,
    vm: entry.chatV2ViewportMode || null,
    cr: entry.chatV2RootPanCorrection ?? null,
    vh: entry.visualViewportHeightRuntime ?? entry.visualViewportHeight ?? null,
    vo: entry.visualViewportOffsetTopRuntime ?? entry.visualViewportOffsetTop ?? null,
    vp: entry.visualViewportPageTopRuntime ?? entry.visualViewportPageTop ?? null,
    sy: entry.windowScrollY ?? null,
    ds: entry.documentScrollTop ?? null,
    tt: toolbarRect?.top ?? null,
    tb: toolbarRect?.bottom ?? null,
    tv: entry.toolbarTopMinusVisualViewportOffset ?? null,
    tc: entry.toolbarComputed?.top || null,
    tpos: entry.toolbarComputed?.position || null,
    tbody: entry.toolbarIsBodyChild ? 1 : 0,
    brT: entry.bodyRect?.top ?? null,
    brH: entry.bodyRect?.height ?? null,
    bp: entry.bodyComputedPosition || null,
    ho: entry.documentElementComputedOverflow || null,
    mmT: entry.mobileMainRuntimeRect?.top ?? null,
    wsT: workspaceRect?.top ?? null,
    wsH: workspaceRect?.height ?? null,
    sT: surfaceRect?.top ?? null,
    sB: surfaceRect?.bottom ?? null,
    sH: surfaceRect?.height ?? null,
    sCH: surfaceComputed?.height || null,
    sMH: surfaceComputed?.maxHeight || null,
    spos: surfaceComputed?.position || null,
    sbody: freshSurfaceSelected
      ? (entry.freshChatSurfaceIsBodyChild ? 1 : 0)
      : (entry.chatV2SurfaceIsBodyChild ? 1 : 0),
    cT: composerRect?.top ?? null,
    cB: composerRect?.bottom ?? null,
    cH: composerRect?.height ?? null,
    sAT: entry.freshChatShellAppliedTop || null,
    sAH: entry.freshChatShellAppliedHeight || null,
    fo: entry.freshChatFocusScrollOwner || null,
    svT: entry.chatV2ViewportTop || null,
    svH: entry.chatV2ViewportHeight || null,
    aT: entry.appliedTop ?? null,
    aH: entry.appliedHeight ?? null,
    gm: entry.geometryMode || null,
    tr: entry.frameTraceReason || null,
    fi: entry.frameTraceIndex ?? null,
  };
}

function compactViewportSignature(entry) {
  const compact = compactViewportEntry(entry);
  return [
    compact.ks,
    compact.kvo,
    compact.vh,
    compact.vo,
    compact.vp,
    compact.sy,
    compact.ds,
    compact.tt,
    compact.tb,
    compact.tv,
    compact.tc,
    compact.tpos,
    compact.tbody,
    compact.brT,
    compact.brH,
    compact.bp,
    compact.ho,
    compact.mmT,
    compact.wsT,
    compact.wsH,
    compact.sT,
    compact.sB,
    compact.sH,
    compact.sCH,
    compact.sMH,
    compact.spos,
    compact.sbody,
    compact.cT,
    compact.cB,
    compact.cH,
    compact.sAT,
    compact.sAH,
    compact.fo,
    compact.svT,
    compact.svH,
    compact.vm,
    compact.cr,
    compact.aT,
    compact.aH,
    compact.gm,
  ].join('|');
}

function buildCompactViewportSummary(entries) {
  const focusStarts = [];
  entries.forEach((entry, index) => {
    if (entry.event === 'focusin') focusStarts.push(index);
  });

  const startIndex = focusStarts.length > 3 ? focusStarts.at(-3) : 0;
  const source = entries.slice(startIndex);
  const selected = [];
  let lastSignature = null;

  for (const entry of source) {
    const signature = compactViewportSignature(entry);
    const changed = signature !== lastSignature;
    const always = SUMMARY_ALWAYS_EVENTS.has(entry.event);
    if (always || changed) selected.push(compactViewportEntry(entry));
    lastSignature = signature;
  }

  const limited = selected.slice(-120);
  const header = {
    schema: MOBILE_CHAT_VIEWPORT_DEBUG_SCHEMA,
    rev: 'release-click-focus-v20',
    total: entries.length,
    from: startIndex,
    count: limited.length,
  };

  return [JSON.stringify(header), ...limited.map((entry) => JSON.stringify(entry))].join('\n');
}

export function mobileChatViewportDebugRequested() {
  try {
    const pageQuery = new URLSearchParams(window.location.search);
    const hashQuery = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const explicitlyRequested = pageQuery.get('chatViewportDebug') === '1'
      || hashQuery.get('chatViewportDebug') === '1';

    if (explicitlyRequested) {
      window.localStorage?.setItem(MOBILE_CHAT_VIEWPORT_DEBUG_STORAGE_KEY, '1');
      return true;
    }

    if (window.localStorage?.getItem(MOBILE_CHAT_VIEWPORT_DEBUG_STORAGE_KEY) === '1') {
      return true;
    }

    return isStandaloneDisplayMode() && isPreviewHost();
  } catch {
    return isStandaloneDisplayMode() && isPreviewHost();
  }
}

async function copyText(text) {
  if (window.navigator.clipboard?.writeText) {
    await window.navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

function readRuntimeDiagnostics() {
  const viewport = window.visualViewport;
  const offsetTop = Number(viewport?.offsetTop || 0);
  const pageTop = Number(viewport?.pageTop || 0);
  const documentScrollTop = Number(document.scrollingElement?.scrollTop || 0);
  const toolbar = document.querySelector(TOOLBAR_SELECTOR);
  const workspace = document.querySelector(WORKSPACE_SELECTOR);
  const mobileMain = workspace?.closest('.mobile-main') || null;
  const chatV2Surface = document.querySelector(CHAT_V2_SURFACE_SELECTOR);
  const chatV2Composer = chatV2Surface?.querySelector(CHAT_V2_COMPOSER_SELECTOR) || null;
  const freshChatSurface = document.querySelector(FRESH_CHAT_SURFACE_SELECTOR);
  const freshChatComposer = freshChatSurface?.querySelector(FRESH_CHAT_COMPOSER_SELECTOR) || null;
  const freshChatInput = freshChatComposer?.querySelector('textarea') || null;
  const toolbarRect = readRect(toolbar);
  const toolbarComputed = toolbar ? window.getComputedStyle(toolbar) : null;
  const chatV2SurfaceComputed = chatV2Surface ? window.getComputedStyle(chatV2Surface) : null;
  const freshChatSurfaceComputed = freshChatSurface ? window.getComputedStyle(freshChatSurface) : null;

  return {
    debugSchema: MOBILE_CHAT_VIEWPORT_DEBUG_SCHEMA,
    toolbarDiagnosticsRevision: 'frame-trace-v2',
    visibilityState: document.visibilityState,
    displayModeStandalone: Boolean(window.matchMedia?.('(display-mode: standalone)').matches),
    navigatorStandalone: window.navigator.standalone === true,
    previewHost: isPreviewHost(),
    userAgent: window.navigator.userAgent,
    screenWidth: Number(window.screen?.width || 0),
    screenHeight: Number(window.screen?.height || 0),
    outerWidth: Number(window.outerWidth || 0),
    outerHeight: Number(window.outerHeight || 0),
    windowScrollX: Number(window.scrollX || 0),
    windowScrollY: Number(window.scrollY || 0),
    documentScrollTop,
    textareaFocusedRuntime: Boolean(document.activeElement?.matches?.(CHAT_INPUT_SELECTOR)),
    visualViewportScale: Number(viewport?.scale || 1),
    visualViewportHeightRuntime: Number(viewport?.height || 0),
    visualViewportOffsetTopRuntime: offsetTop,
    visualViewportPageTopRuntime: pageTop,
    visualViewportPageTopMinusOffsetTop: pageTop - offsetTop,
    toolbarRect,
    toolbarTopMinusVisualViewportOffset: toolbarRect ? toolbarRect.top - offsetTop : null,
    toolbarParent: describeElement(toolbar?.parentElement || null),
    toolbarIsBodyChild: toolbar?.parentElement === document.body,
    toolbarOwnerDataset: toolbar?.dataset.toolbarOwner || null,
    toolbarInlineStyle: toolbar?.getAttribute('style') || '',
    toolbarComputed: toolbarComputed ? {
      position: toolbarComputed.position,
      top: toolbarComputed.top,
      right: toolbarComputed.right,
      left: toolbarComputed.left,
      transform: toolbarComputed.transform,
    } : null,
    bodyRect: readRect(document.body),
    bodyComputedPosition: window.getComputedStyle(document.body).position,
    documentElementComputedOverflow: window.getComputedStyle(document.documentElement).overflow,
    mobileMainRuntimeRect: readRect(mobileMain),
    workspaceRuntimeRect: readRect(workspace),
    keyboardStateRuntime: chatV2Surface?.dataset.keyboardState
      || workspace?.dataset.keyboardState
      || null,
    chatV2ViewportMode: chatV2Surface?.dataset.viewportMode || null,
    chatV2RootPanCorrection: Number(chatV2Surface?.dataset.rootPanCorrection || 0),
    chatV2SurfaceRect: readRect(chatV2Surface),
    chatV2SurfaceComputed: chatV2SurfaceComputed ? {
      position: chatV2SurfaceComputed.position,
      top: chatV2SurfaceComputed.top,
      height: chatV2SurfaceComputed.height,
      minHeight: chatV2SurfaceComputed.minHeight,
      maxHeight: chatV2SurfaceComputed.maxHeight,
      overflow: chatV2SurfaceComputed.overflow,
    } : null,
    chatV2ComposerRect: readRect(chatV2Composer),
    chatV2SurfaceIsBodyChild: chatV2Surface?.parentElement === document.body,
    freshChatSurfaceRect: readRect(freshChatSurface),
    freshChatComposerRect: readRect(freshChatComposer),
    freshChatSurfaceComputed: freshChatSurfaceComputed ? {
      position: freshChatSurfaceComputed.position,
      top: freshChatSurfaceComputed.top,
      height: freshChatSurfaceComputed.height,
      minHeight: freshChatSurfaceComputed.minHeight,
      maxHeight: freshChatSurfaceComputed.maxHeight,
      overflow: freshChatSurfaceComputed.overflow,
    } : null,
    freshChatSurfaceIsBodyChild: freshChatSurface?.parentElement === document.body,
    freshChatKeyboardPhase: freshChatSurface?.dataset.keyboardPhase || null,
    freshChatKeyboardVisualOpen: freshChatSurface?.dataset.keyboardVisualOpen || null,
    freshChatViewportSettled: freshChatSurface?.dataset.viewportSettled || null,
    freshChatShellAppliedTop: freshChatSurface?.style.getPropertyValue('--raid-mobile-chat-shell-top') || null,
    freshChatShellAppliedHeight: freshChatSurface?.style.getPropertyValue('--raid-mobile-chat-shell-height') || null,
    freshChatFocusScrollOwner: freshChatInput?.dataset.focusScrollOwner || null,
    chatV2ViewportTop: null,
    chatV2ViewportHeight: chatV2Surface?.style.getPropertyValue('--raid-mobile-chat-visible-height') || null,
    serviceWorkerController: window.navigator.serviceWorker?.controller?.scriptURL || null,
    loadedModuleScripts: [...document.scripts]
      .map((script) => script.src)
      .filter(Boolean)
      .slice(-8),
  };
}

export function createMobileChatViewportDebugger({ enabled }) {
  const entries = [];
  if (!enabled) {
    return {
      entries,
      log() {},
      destroy() {},
    };
  }

  window[MOBILE_CHAT_VIEWPORT_DEBUG_GLOBAL_KEY] = entries;

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'raid-control-chat-viewport-debug-trigger';
  trigger.textContent = `Viewport ${MOBILE_CHAT_VIEWPORT_DEBUG_SCHEMA}`;

  const panel = document.createElement('section');
  panel.className = 'raid-control-chat-viewport-debug-panel';
  panel.hidden = true;
  panel.innerHTML = `
    <header>
      <strong>iOS 채팅 Viewport · ${MOBILE_CHAT_VIEWPORT_DEBUG_SCHEMA}</strong>
      <div class="raid-control-chat-viewport-debug-actions">
        <button type="button" data-action="copy-summary">요약 복사</button>
        <button type="button" data-action="copy-full">전체 복사</button>
        <button type="button" data-action="close">닫기</button>
      </div>
    </header>
    <pre class="raid-control-chat-viewport-debug-current">대기 중</pre>
    <pre class="raid-control-chat-viewport-debug-events">이벤트 없음</pre>
  `;

  const current = panel.querySelector('.raid-control-chat-viewport-debug-current');
  const events = panel.querySelector('.raid-control-chat-viewport-debug-events');
  const summaryCopyButton = panel.querySelector('[data-action="copy-summary"]');
  const fullCopyButton = panel.querySelector('[data-action="copy-full"]');
  const closeButton = panel.querySelector('[data-action="close"]');
  let frameTraceAnimationFrame = 0;
  let frameTraceGeneration = 0;

  const render = () => {
    const latest = entries.at(-1) || null;
    current.textContent = latest ? JSON.stringify(latest, null, 2) : '대기 중';
    events.textContent = entries.length
      ? entries.slice(-30).map((entry) => JSON.stringify(entry)).join('\n')
      : '이벤트 없음';
  };

  const appendEntry = (entry) => {
    const enrichedEntry = {
      ...readRuntimeDiagnostics(),
      ...entry,
    };
    entries.push(enrichedEntry);
    if (entries.length > 1200) entries.splice(0, entries.length - 1200);
    console.debug('[DoroParty chat viewport]', enrichedEntry);
    if (!panel.hidden) render();
  };

  const startFrameTrace = (reason) => {
    frameTraceGeneration += 1;
    const generation = frameTraceGeneration;
    if (frameTraceAnimationFrame) window.cancelAnimationFrame(frameTraceAnimationFrame);
    frameTraceAnimationFrame = 0;
    let frameIndex = 0;

    const sampleFrame = () => {
      if (generation !== frameTraceGeneration) return;
      frameIndex += 1;
      appendEntry({
        event: 'frame.trace',
        timestamp: Date.now(),
        performanceNow: Math.round(window.performance?.now?.() || 0),
        textareaFocused: Boolean(document.activeElement?.matches?.(CHAT_INPUT_SELECTOR)),
        keyboardState: document.querySelector(CHAT_V2_SURFACE_SELECTOR)?.dataset.keyboardState
          || document.querySelector(WORKSPACE_SELECTOR)?.dataset.keyboardState
          || null,
        frameTraceReason: reason,
        frameTraceIndex: frameIndex,
      });
      if (frameIndex < FRAME_TRACE_MAX_FRAMES) {
        frameTraceAnimationFrame = window.requestAnimationFrame(sampleFrame);
      } else {
        frameTraceAnimationFrame = 0;
      }
    };

    frameTraceAnimationFrame = window.requestAnimationFrame(sampleFrame);
  };

  const openPanel = () => {
    panel.hidden = false;
    render();
  };
  const closePanel = () => {
    panel.hidden = true;
  };
  const copyWithFeedback = async (button, text) => {
    const originalText = button.textContent;
    try {
      await copyText(text);
      button.textContent = '복사됨';
    } catch {
      button.textContent = '복사 실패';
    }
    window.requestAnimationFrame(() => {
      button.textContent = originalText;
    });
  };
  const handleSummaryCopy = () => copyWithFeedback(summaryCopyButton, buildCompactViewportSummary(entries));
  const handleFullCopy = () => copyWithFeedback(fullCopyButton, JSON.stringify(entries, null, 2));

  trigger.addEventListener('click', openPanel);
  closeButton.addEventListener('click', closePanel);
  summaryCopyButton.addEventListener('click', handleSummaryCopy);
  fullCopyButton.addEventListener('click', handleFullCopy);
  document.body.append(trigger, panel);

  return {
    entries,
    log(entry) {
      appendEntry(entry);
      if (FRAME_TRACE_TRIGGER_EVENTS.has(entry.event)) startFrameTrace(entry.event);
    },
    destroy() {
      frameTraceGeneration += 1;
      if (frameTraceAnimationFrame) window.cancelAnimationFrame(frameTraceAnimationFrame);
      trigger.removeEventListener('click', openPanel);
      closeButton.removeEventListener('click', closePanel);
      summaryCopyButton.removeEventListener('click', handleSummaryCopy);
      fullCopyButton.removeEventListener('click', handleFullCopy);
      trigger.remove();
      panel.remove();
      delete window[MOBILE_CHAT_VIEWPORT_DEBUG_GLOBAL_KEY];
    },
  };
}
